import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip";
import {
  NotificationEmptyState,
  NotificationItem,
} from "@/features/notifications/notification-item";
import { useNotifications } from "@/hooks/use-notifications";

/**
 * Topbar notification bell (NOT-00) — trigger plus its dropdown panel.
 *
 * Ported from `.design-ref/prototype/components/shell/notification-bell.tsx`.
 * The design read a zustand `notification-store` seeded with sample data; here
 * the panel reads the real `GET /v1/notifications` feed through
 * `useNotifications()` and marks a row read with the existing
 * `POST /v1/notifications/{id}/read` call (ALL-4747 G6). Nothing is fabricated:
 * the empty panel is the API's empty feed, and a failed read renders an error
 * state with a retry rather than an empty list.
 *
 * Owns BOTH the trigger and the panel (rather than receiving `open` from
 * Topbar) for one reason: outside-click dismissal. The `pointerdown`
 * handler tests containment against this component's own root, which
 * includes the trigger, so clicking the bell while the panel is open
 * toggles it shut instead of the close-then-toggle double-flip you get
 * when the trigger and panel live in separate subtrees.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const { items, unreadCount: unread, loading, error, refresh, canMarkRead, markRead, markAllRead } = useNotifications();

  // While an approval notification is unread, the bell pulses like the
  // approval row itself — the design's `selectPendingApproval`, derived here
  // from the live feed's own rows rather than a client-side store.
  const pendingApproval = items.some((n) => n.kind === "approval" && !n.read);

  const close = useCallback(() => setOpen(false), []);

  // The panel is a dialog, so opening it moves focus into it — otherwise a
  // keyboard user stays on the trigger and the panel's controls are only
  // reachable by tabbing past the rest of the topbar. Escape still hands focus
  // back to the trigger (the keydown handler below).
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    const first = panel.querySelector<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    // With every control disabled (the loading branch) the panel itself takes
    // focus, so the dialog is never a focus black hole.
    (first ?? panel).focus();
  }, [open]);

  useEffect(() => {
    function onPointerDown(e: MouseEvent | TouchEvent) {
      if (!rootRef.current?.contains(e.target as Node)) close();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (open && e.key === "Escape") {
        close();
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  const label = unread > 0 ? `Notifications (${unread} unread)` : "Notifications";

  return (
    <div ref={rootRef} className="relative">
      <Tooltip content={label}>
        <Button
          ref={triggerRef}
          type="button"
          variant="ghost"
          size="md"
          iconOnly
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="relative rounded-md max-lg:border max-lg:border-line max-lg:bg-surface"
        >
          {pendingApproval && (
            <span aria-hidden className="notif-pulse pointer-events-none absolute inset-1.5 rounded-full" />
          )}
          <Bell aria-hidden className="size-4" />
          {unread > 0 && (
            <span
              aria-hidden
              className="absolute right-1.5 top-1.5 size-2 rounded-full border-2 border-surface bg-danger"
            />
          )}
        </Button>
      </Tooltip>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Notifications"
          tabIndex={-1}
          data-testid="notification-panel"
          /* Below lg the panel is pinned to the viewport instead of the trigger:
   on narrow layouts the bell sits mid-topbar (the account dropdown
   follows it), so `right-0` would push the panel off the left edge. At lg+
   there is room to anchor to the bell as before. */
          className="fixed left-3 top-[60px] z-50 w-[360px] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-lg border border-line bg-surface shadow md:top-[92px] lg:absolute lg:left-auto lg:right-0 lg:top-auto lg:mt-2 lg:max-w-none"
        >
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <span className="flex items-baseline gap-2">
              <span className="text-sm font-semibold text-ink">Notifications</span>
              <span className="text-xs text-muted">
                {/* A failed fetch never reads as "All caught up": an unknown
                    feed must not be presented as an empty one. */}
                {loading ? "Loading…" : error ? "Unavailable" : unread > 0 ? `${unread} unread` : "All caught up"}
              </span>
            </span>
            {canMarkRead && <button
              type="button"
              onClick={() => { void markAllRead(); }}
              /* Disabled while the feed is unknown (loading or failed): with no
                 rows there is nothing to mark, and a write from an error state
                 would be a blind POST. */
              disabled={loading || error !== null || unread === 0}
              className="text-xs font-semibold text-primary underline-offset-2 hover:underline disabled:text-muted disabled:opacity-60"
            >
              Mark all as read
            </button>}
          </div>

          {loading ? (
            <div className="px-4 py-6" data-testid="notification-loading">
              <div className="spinner mx-auto" role="status" aria-label="Loading notifications" />
            </div>
          ) : error ? (
            <div className="px-4 py-10 text-center" role="alert" data-testid="notification-error">
              <p className="text-sm font-semibold text-ink">Could not load notifications</p>
              <p className="mx-auto mt-1 max-w-[30ch] text-xs leading-snug text-muted">
                The notification feed did not respond. Try again in a moment.
              </p>
              <Button type="button" variant="secondary" size="sm" className="mt-3" onClick={() => refresh()}>
                Try again
              </Button>
            </div>
          ) : items.length === 0 ? (
            <NotificationEmptyState />
          ) : (
            <ul className="max-h-[min(380px,50vh)] overflow-y-auto">
              {items.map((n) => (
                <li key={n.id} className="border-b border-line last:border-b-0">
                  <NotificationItem
                    notification={n}
                    readOnly={!canMarkRead}
                    onActivate={() => {
                      if (canMarkRead) void markRead(n.id);
                      close();
                    }}
                  />
                </li>
              ))}
            </ul>
          )}

          <div className="border-t border-line px-4 py-2.5">
            <Link
              to="/notifications"
              onClick={close}
              className="block w-full text-center text-sm font-semibold text-primary underline-offset-2 hover:underline"
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
